import { FC } from 'react';
import { Box, Chip, FormGroup, FormLabel, FormHelperText } from '@mui/material';
import { useForm } from '@carcareers/contexts';
import { CheckBoxInput } from './CheckBoxInput';

export interface CheckboxOption {
  label: string;
  value: string | number;
}

export interface CheckboxListInputProps {
  label?: string;
  name: string;
  options: CheckboxOption[];
  required?: boolean;
  variant?: 'checkbox' | 'chip';
  disabled?: boolean;
}

export const CheckboxListInput: FC<CheckboxListInputProps> = ({
  label,
  name,
  options,
  required,
  disabled,
  variant = 'checkbox',
}) => {
  const { setData, getData, getErrors } = useForm();

  const handleToggle = (optionValue: string | number) => {
    const data = getData<string[]>(name);
    const newValues = data?.includes(optionValue.toString())
      ? data?.filter((value: string | number) => value !== optionValue)
      : [...(data ?? []), optionValue];

    setData(name, newValues);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      {variant === 'checkbox' ? (
        <FormGroup>
          {options.map((option) => (
            <CheckBoxInput
              key={option.value}
              label={option.label}
              name={`${name}.${option.value}`}
              disabled={disabled}
            />
          ))}
        </FormGroup>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {options.map((option) => (
            <Chip
              disabled={disabled}
              key={option.value}
              label={option.label}
              color={getData<string[]>(name)?.includes(option.value.toString()) ? 'primary' : 'default'}
              onClick={() => handleToggle(option.value)}
              variant={getData<string[]>(name)?.includes(option.value.toString()) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      )}
      {getErrors(name) && <FormHelperText error>{getErrors(name)}</FormHelperText>}
    </Box>
  );
};
